import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from '../../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../../types/constants';
import { useInsightsState, useInsightsActions } from '../../../hooks/useInsights';
import { useShelterState } from '../../../hooks/useShelter';
import { useDebugActions } from '../../../hooks/useDebugActions';
import { OptInRateCard } from '../../../components/insights/OptInRateCard';
import { AdoptionsCard } from '../../../components/insights/AdoptionsCard';
import { OptInRateCardSkeleton } from '../../../components/insights/OptInRateCardSkeleton';
import { AdoptionsCardSkeleton } from '../../../components/insights/AdoptionsCardSkeleton';
import { Icon, IconName } from '@hills-design-system/components';
import { TimePeriod } from '../../../types/insights';
import './Insights.css';
import { convertUTCToLocalDate } from '../../../utils/dateUtils';
import { StatusRoundel } from '../../../components/StatusRoundel/StatusRoundel';
import { isAdminDateOverrideEnabled, isReportingPaused } from '../../../utils/featureFlags';
import { ReportingPausedRoundel } from '../../../components/ReportingPaused/ReportingPausedRoundel';

interface InsightsDropdownOption {
  value: string;
  label: string;
}

interface InsightsDropdownProps {
  options: InsightsDropdownOption[];
  selectedValue?: string | null;
  onSelect: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
  className?: string;
}

const MONTH_ABBREVIATIONS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const QUARTER_LABELS = ['Q1', 'Q2', 'Q3', 'Q4'];

const getQuarterLabel = (quarterIndex: number, year: number): string =>
  `${QUARTER_LABELS[quarterIndex]} ${year}`;

const getQuarterIndexFromValue = (quarter: string): number | null => {
  const match = RegExp(/Q(\d)/i).exec(quarter);
  if (!match) {
    return null;
  }
  const parsed = Number(match[1]);
  if (Number.isNaN(parsed)) {
    return null;
  }
  return parsed - 1;
};

const removeYearFromLabel = (value: string): string => value.replace(/\s+\d{4}$/, '');

const getMonthAbbreviation = (monthIndex: number): string => MONTH_ABBREVIATIONS[monthIndex];

const sortQuarterValuesDescending = (values: string[]): string[] =>
  [...values].sort(
    (a, b) => (getQuarterIndexFromValue(b) ?? 0) - (getQuarterIndexFromValue(a) ?? 0)
  );

const sortMonthIndicesDescending = (indices: number[]): number[] =>
  [...indices].sort((a, b) => b - a);

const InsightsDropdown = ({
  options,
  selectedValue,
  onSelect,
  placeholder,
  disabled = false,
  className = '',
}: InsightsDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedOption = options.find((option) => option.value === selectedValue);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setIsOpen((prev) => !prev);
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setIsOpen(false);
    }
  };

  const handleSelect = (value: string) => {
    onSelect(value);
    setIsOpen(false);
  };

  return (
    <div className={`insights-dropdown-container ${className}`} ref={containerRef}>
      <button
        type="button"
        className={`insights-dropdown-trigger ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={selectedOption ? 'value' : 'placeholder'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <Icon icon={isOpen ? IconName.ChevronUp : IconName.ChevronDown} size={16} />
      </button>

      {isOpen && options.length > 0 && (
        <div className="insights-dropdown-menu" role="listbox">
          {options.map((option) => {
            const isSelected = option.value === selectedValue;
            return (
              <button
                type="button"
                key={option.value}
                className={`insights-dropdown-option ${isSelected ? 'selected' : ''}`}
                onClick={() => handleSelect(option.value)}
                role="option"
                aria-selected={isSelected}
              >
                <span>{option.label}</span>
                {isSelected && <Icon icon={IconName.Check} size={16} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Insights: React.FC = () => {
  const { t } = useTranslation();
  const { insights, isLoading, isRefreshing, error } = useInsightsState();
  const { getDashboardInsights } = useInsightsActions();
  const { shelter } = useShelterState();
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>(TimePeriod.YEAR);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedQuarter, setSelectedQuarter] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const isInitialLoadRef = useRef<boolean>(true);
  const shouldSkipNextFetchRef = useRef<boolean>(false);
  const prevSelectedYearRef = useRef<number | null>(null);

  const { getDateOverride } = useDebugActions();
  const [currentDate, setCurrentDate] = useState(() => new Date());

  const dateOverrideEnabled = isAdminDateOverrideEnabled();

  useEffect(() => {
    if (!dateOverrideEnabled) return;

    const fetchDateOverride = async () => {
      try {
        const response = await getDateOverride();
        if (response.isActive && response.overrideDate) {
          const newDate = convertUTCToLocalDate(response.overrideDate);
          if (newDate) {
            setCurrentDate(newDate);
            setSelectedYear(newDate.getFullYear());
          }
        }
      } catch (error) {
        console.error('Failed to load date override:', error);
      }
    };
    fetchDateOverride();
  }, [getDateOverride, dateOverrideEnabled]);

  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  const currentQuarterIndex = Math.floor(currentMonth / 3);

  const joinDate = useMemo(() => {
    if (!shelter?.join_program_date) {
      return null;
    }
    return convertUTCToLocalDate(shelter.join_program_date);
  }, [shelter?.join_program_date]);

  const yearOptions = useMemo<InsightsDropdownOption[]>(() => {
    const startYear = joinDate ? joinDate.getFullYear() : currentYear;
    const endYear = currentYear;
    const fromYear = Math.min(startYear, endYear);
    const toYear = Math.max(startYear, endYear);
    const years: InsightsDropdownOption[] = [];

    for (let year = toYear; year >= fromYear; year -= 1) {
      years.push({
        value: year.toString(),
        label: year.toString(),
      });
    }

    return years;
  }, [currentYear, joinDate]);

  const quarterValuesByYear = useMemo<Record<number, string[]>>(() => {
    const record: Record<number, string[]> = {};

    const startYear = joinDate ? joinDate.getFullYear() : currentYear;
    const startMonth = joinDate ? joinDate.getMonth() : 0;
    const endYear = currentYear;

    const fromYear = Math.min(startYear, endYear);
    const toYear = Math.max(startYear, endYear);

    for (let year = fromYear; year <= toYear; year += 1) {
      let firstQuarterIndex = 0;
      let lastQuarterIndex = 3;

      if (year === startYear) {
        firstQuarterIndex = Math.floor(startMonth / 3);
      }

      if (year === endYear) {
        lastQuarterIndex = currentQuarterIndex;
      }

      if (firstQuarterIndex > lastQuarterIndex) {
        continue;
      }

      record[year] = [];
      for (
        let quarterIndex = firstQuarterIndex;
        quarterIndex <= lastQuarterIndex;
        quarterIndex += 1
      ) {
        record[year].push(getQuarterLabel(quarterIndex, year));
      }
    }

    return record;
  }, [currentQuarterIndex, currentYear, joinDate]);

  const monthValuesByYear = useMemo<Record<number, number[]>>(() => {
    const record: Record<number, number[]> = {};

    const startYear = joinDate ? joinDate.getFullYear() : currentYear;
    const startMonth = joinDate ? joinDate.getMonth() : 0;
    const endYear = currentYear;

    const fromYear = Math.min(startYear, endYear);
    const toYear = Math.max(startYear, endYear);

    for (let year = fromYear; year <= toYear; year += 1) {
      let firstMonthIndex = 0;
      let lastMonthIndex = 11;

      if (year === startYear) {
        firstMonthIndex = startMonth;
      }

      if (year === endYear) {
        lastMonthIndex = currentMonth;
      }

      if (firstMonthIndex > lastMonthIndex) {
        continue;
      }

      record[year] = [];
      for (let monthIndex = firstMonthIndex; monthIndex <= lastMonthIndex; monthIndex += 1) {
        record[year].push(monthIndex);
      }
    }

    return record;
  }, [currentMonth, currentYear, joinDate]);

  const quarterOptions = useMemo<InsightsDropdownOption[]>(() => {
    if (selectedYear === null) {
      return [];
    }
    const quarterValues = quarterValuesByYear[selectedYear] ?? [];
    const displayValues = sortQuarterValuesDescending(quarterValues);
    return displayValues.map((quarter) => ({
      value: quarter,
      label: removeYearFromLabel(quarter),
    }));
  }, [quarterValuesByYear, selectedYear]);

  const monthOptions = useMemo<InsightsDropdownOption[]>(() => {
    if (selectedYear === null) {
      return [];
    }
    const monthIndices = monthValuesByYear[selectedYear] ?? [];
    const displayIndices = sortMonthIndicesDescending(monthIndices);
    return displayIndices.map((monthIndex) => {
      const abbreviation = getMonthAbbreviation(monthIndex);
      return {
        value: abbreviation,
        label: abbreviation,
      };
    });
  }, [monthValuesByYear, selectedYear]);

  const secondaryOptions = useMemo<InsightsDropdownOption[]>(() => {
    if (selectedPeriod === TimePeriod.QUARTER) {
      return quarterOptions;
    }

    if (selectedPeriod === TimePeriod.MONTH) {
      return monthOptions;
    }

    return [];
  }, [monthOptions, quarterOptions, selectedPeriod]);

  const getDefaultQuarterForYear = useCallback(
    (year: number, quarterValues: string[]): string | null => {
      if (quarterValues.length === 0) {
        return null;
      }
      const sortedDescending = sortQuarterValuesDescending(quarterValues);

      if (year === currentYear) {
        const currentQuarterLabel = getQuarterLabel(currentQuarterIndex, year);
        const matchingQuarter = sortedDescending.find((value) => value === currentQuarterLabel);
        if (matchingQuarter) {
          return matchingQuarter;
        }
        return sortedDescending[0] ?? null;
      }

      const lastQuarter = sortedDescending[sortedDescending.length - 1] ?? null;
      return lastQuarter;
    },
    [currentQuarterIndex, currentYear]
  );

  const getDefaultMonthForYear = useCallback(
    (year: number, monthIndices: number[]): string | null => {
      if (monthIndices.length === 0) {
        return null;
      }
      const sortedDescending = sortMonthIndicesDescending(monthIndices);

      if (year === currentYear) {
        if (sortedDescending.includes(currentMonth)) {
          return getMonthAbbreviation(currentMonth);
        }
        return sortedDescending.length > 0 ? getMonthAbbreviation(sortedDescending[0]) : null;
      }

      const lastMonthIndex = sortedDescending[sortedDescending.length - 1];
      return lastMonthIndex !== undefined ? getMonthAbbreviation(lastMonthIndex) : null;
    },
    [currentMonth, currentYear]
  );

  useEffect(() => {
    if (yearOptions.length === 0) {
      setSelectedYear(null);
      return;
    }

    const availableYears = yearOptions.map((option) => Number(option.value));
    const preferredYear = availableYears.find((year) => year === currentYear) ?? availableYears[0];

    setSelectedYear((prev) => {
      if (prev !== null && availableYears.includes(prev)) {
        return prev;
      }
      return preferredYear;
    });
  }, [currentYear, yearOptions]);

  useEffect(() => {
    if (selectedYear === null) {
      if (selectedQuarter !== null || selectedMonth !== null) {
        shouldSkipNextFetchRef.current = true;
        setSelectedQuarter(null);
        setSelectedMonth(null);
      }
      prevSelectedYearRef.current = null;
      return;
    }

    const yearChanged = selectedYear !== prevSelectedYearRef.current;
    prevSelectedYearRef.current = selectedYear;

    const quarterValues = quarterValuesByYear[selectedYear] ?? [];
    const monthIndices = monthValuesByYear[selectedYear] ?? [];
    const isQuarterValid = selectedQuarter !== null && quarterValues.includes(selectedQuarter);

    const monthIndex = selectedMonth ? MONTH_ABBREVIATIONS.indexOf(selectedMonth) : -1;
    const isMonthValid =
      selectedMonth !== null && monthIndex !== -1 && monthIndices.includes(monthIndex);

    let shouldSkipNextFetch = false;

    if (yearChanged || !isQuarterValid) {
      let targetQuarter: string | null = null;
      if (selectedQuarter) {
        const currentLabel = removeYearFromLabel(selectedQuarter);
        targetQuarter = quarterValues.find((q) => removeYearFromLabel(q) === currentLabel) ?? null;
      }

      const nextQuarter = targetQuarter ?? getDefaultQuarterForYear(selectedYear, quarterValues);

      if (nextQuarter !== selectedQuarter) {
        if (selectedPeriod === TimePeriod.QUARTER) {
          shouldSkipNextFetch = true;
        }
        setSelectedQuarter(nextQuarter);
      }
    }

    if (!isMonthValid) {
      const defaultMonth = getDefaultMonthForYear(selectedYear, monthIndices);
      if (defaultMonth !== selectedMonth) {
        if (selectedPeriod === TimePeriod.MONTH) {
          shouldSkipNextFetch = true;
        }
        setSelectedMonth(defaultMonth);
      }
    }

    if (shouldSkipNextFetch) {
      shouldSkipNextFetchRef.current = true;
    }
  }, [
    getDefaultMonthForYear,
    getDefaultQuarterForYear,
    monthValuesByYear,
    quarterValuesByYear,
    selectedMonth,
    selectedQuarter,
    selectedYear,
    selectedPeriod,
  ]);

  const isYearDropdownDisabled = yearOptions.length === 0;
  const isSecondaryDropdownDisabled =
    selectedPeriod !== TimePeriod.YEAR && secondaryOptions.length === 0;

  const handleYearSelect = (value: string) => {
    const parsedValue = Number(value);
    if (!Number.isNaN(parsedValue)) {
      setSelectedYear(parsedValue);
    }
  };

  const handleSecondarySelect = (value: string) => {
    if (selectedPeriod === TimePeriod.QUARTER) {
      setSelectedQuarter(value);
    } else if (selectedPeriod === TimePeriod.MONTH) {
      setSelectedMonth(value);
    }
  };

  useEffect(() => {
    // set page title
    document.title = t(TranslationKeyEnum.Insights);
  }, [t]);

  // Fetch insights data when period changes
  useEffect(() => {
    // For YEAR period, only selectedYear is required
    // For QUARTER and MONTH periods, both selectedYear and selectedQuarter/selectedMonth are required
    const hasRequiredData =
      selectedYear !== null &&
      (selectedPeriod === TimePeriod.YEAR ||
        (selectedPeriod === TimePeriod.QUARTER && selectedQuarter !== null) ||
        (selectedPeriod === TimePeriod.MONTH && selectedMonth !== null));

    if (!hasRequiredData) {
      return;
    }

    if (shouldSkipNextFetchRef.current) {
      shouldSkipNextFetchRef.current = false;
      return;
    }

    if (hasRequiredData) {
      const query = {
        period: selectedPeriod,
        selectedYear: selectedYear ?? undefined,
        selectedQuarter:
          selectedPeriod === TimePeriod.QUARTER ? (selectedQuarter ?? undefined) : undefined,
        selectedMonth:
          selectedPeriod === TimePeriod.MONTH ? (selectedMonth ?? undefined) : undefined,
      };
      getDashboardInsights(query, !isInitialLoadRef.current);
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
      }
    }
  }, [getDashboardInsights, selectedPeriod, selectedYear, selectedQuarter, selectedMonth]);

  if (isLoading && !insights) {
    return (
      <div className="w-full bg-white rounded-2xl border border-black/20 p-12">
        <div className="flex justify-center items-center h-40">
          <div className="text-center">
            <h3 className="text-xl font-semibold mb-2">{t(TranslationKeyEnum.Insights)}</h3>
            <p className="text-black/60">{t(TranslationKeyEnum.Loading)}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full bg-white rounded-2xl border border-black/20 p-12">
        <div className="flex justify-center items-center h-40">
          <div className="text-center">
            <h3 className="text-xl font-semibold mb-2">{t(TranslationKeyEnum.Insights)}</h3>
            <p className="text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!insights && !isLoading) {
    return (
      <div className="w-full bg-white rounded-2xl border border-black/20 p-12">
        <div className="flex justify-center items-center h-40">
          <div className="text-center">
            <h3 className="text-xl font-semibold mb-2">{t(TranslationKeyEnum.Insights)}</h3>
            <p className="text-black/60">{t(TranslationKeyEnum.NoInsightsDataAvailable)}</p>
          </div>
        </div>
      </div>
    );
  }

  const showComplianceWarning = insights?.complianceWarning?.show === true;
  const lastDataReceivedDate = insights?.lastDataReceivedDate;

  return (
    <div className="flex flex-col gap-6 pb-10 w-full">
      {/* Time Period Selector */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="insights-period-toggle">
            <button
              onClick={() => {
                setSelectedPeriod(TimePeriod.YEAR);
                setSelectedQuarter(null);
                setSelectedMonth(null);
              }}
              className={`insights-period-button ${
                selectedPeriod === TimePeriod.YEAR ? 'active' : ''
              }`}
            >
              {t(TranslationKeyEnum.Year)}
            </button>
            <button
              onClick={() => {
                setSelectedPeriod(TimePeriod.QUARTER);
                if (selectedYear !== null) {
                  const quarterValues = quarterValuesByYear[selectedYear] ?? [];
                  const defaultQuarter = getDefaultQuarterForYear(selectedYear, quarterValues);
                  if (defaultQuarter !== selectedQuarter) {
                    shouldSkipNextFetchRef.current = true;
                    setSelectedQuarter(defaultQuarter);
                  }
                }
              }}
              className={`insights-period-button ${
                selectedPeriod === TimePeriod.QUARTER ? 'active' : ''
              }`}
            >
              {t(TranslationKeyEnum.Quarter)}
            </button>
            <button
              onClick={() => {
                setSelectedPeriod(TimePeriod.MONTH);
                if (selectedYear !== null) {
                  const monthIndices = monthValuesByYear[selectedYear] ?? [];
                  const defaultMonth = getDefaultMonthForYear(selectedYear, monthIndices);
                  if (defaultMonth !== selectedMonth) {
                    shouldSkipNextFetchRef.current = true;
                    setSelectedMonth(defaultMonth);
                  }
                }
              }}
              className={`insights-period-button ${
                selectedPeriod === TimePeriod.MONTH ? 'active' : ''
              }`}
            >
              {t(TranslationKeyEnum.Month)}
            </button>
          </div>

          {/* Dynamic Selectors */}
          <div className="flex items-center gap-4">
            <InsightsDropdown
              className="w-[170px]"
              options={yearOptions}
              selectedValue={selectedYear !== null ? selectedYear.toString() : undefined}
              onSelect={handleYearSelect}
              disabled={isYearDropdownDisabled}
              placeholder={t(TranslationKeyEnum.Loading)}
            />

            {(selectedPeriod === TimePeriod.QUARTER || selectedPeriod === TimePeriod.MONTH) && (
              <InsightsDropdown
                className="w-[170px]"
                options={secondaryOptions}
                selectedValue={
                  selectedPeriod === TimePeriod.QUARTER
                    ? (selectedQuarter ?? undefined)
                    : (selectedMonth ?? undefined)
                }
                onSelect={handleSecondarySelect}
                disabled={isSecondaryDropdownDisabled}
                placeholder={t(TranslationKeyEnum.Loading)}
              />
            )}
          </div>
        </div>

        {/* Status Roundel - Right Aligned */}
        {shelter?.is_petszel_enabled &&
          (isReportingPaused() ? (
            <ReportingPausedRoundel tooltipAlign="left" />
          ) : (
            !showComplianceWarning && <StatusRoundel lastDataReceivedDate={lastDataReceivedDate} />
          ))}
      </div>

      {/* Cards */}
      <div className="flex flex-col gap-6">
        {isRefreshing || !insights ? (
          <OptInRateCardSkeleton />
        ) : (
          <OptInRateCard data={insights.optInRate} />
        )}

        {isRefreshing || !insights ? (
          <AdoptionsCardSkeleton />
        ) : (
          <AdoptionsCard
            data={insights.adoptions}
            selectedPeriod={selectedPeriod}
            selectedYear={selectedYear}
            selectedMonth={selectedMonth}
            selectedQuarter={selectedQuarter}
          />
        )}
      </div>
    </div>
  );
};

export default Insights;
